import {
  buildChartCalculationPayload,
  buildChartHandlerRegistry,
  CHART_CALCULATION_TIMEOUT_MS,
  createChartCalculationDispatcher,
  type TChartCalculationHandler,
} from '@dbl/services/common/chartCalculationCore';
import {
  datalabHttp,
  datalabWithTokenHttp,
  type TEmptyMutationData,
  type TDatalabHttpConfig,
} from '@dbl/services/common';
import { analysisChartConfigByType } from './config';
import { prepareAnalysisChartRequest } from './request';
import {
  normalizeResponseDto,
  transformAnalysisChartResponseDtoToVo,
} from './response';
import type {
  IAnalysisChartRequest,
  IAnalysisChartUploadVoMap,
  ICancelAnalysisRequest,
  TAnalysisChartUploadVo,
} from './request';
import type {
  IAnalysisChartResponseDto,
  IAnalysisChartResponseDtoMap,
  TAnalysisChartMapKey,
  TAnalysisChartResponseDtoOf,
  TAnalysisChartResultVoOf,
} from './response';

/**
 * 取消进行中的分析计算（`analysis_cancel`）。
 *
 * 仅通知后端中止任务；调用方只关心成功/失败（`.finally` / `try-catch`），不消费响应体。
 */
export const cancelAnalysis = (
  analysisReqId: string,
): Promise<TEmptyMutationData> => {
  const data: ICancelAnalysisRequest = {
    query_sequence_id: analysisReqId,
  };
  return datalabHttp<TEmptyMutationData>({
    url: '/api/v1/datalab/analysis_cancel',
    method: 'post',
    data,
  });
};

/**
 * 作用：旧版分析计算接口（v1 analysis），保留用于兼容旧调用链路。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getDataAnlysis
 * 原始引用（used-by）：（待后续 grep 完整补齐）
 */
export const getDataAnlysis: TChartCalculationHandler<
  IAnalysisChartRequest,
  IAnalysisChartResponseDto
> = (param) =>
  datalabHttp({
    url: '/api/v1/datalab/analysis',
    method: 'post',
    data: param,
    timeout: CHART_CALCULATION_TIMEOUT_MS,
  });

/**
 * 作用：旧版全量分析计算接口（analysis_full），保留兼容。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getDataAnlysisFull
 * 原始引用（used-by）：（待后续 grep 完整补齐）
 */
export const getDataAnlysisFull: TChartCalculationHandler<
  IAnalysisChartRequest,
  IAnalysisChartResponseDto
> = (param) =>
  datalabWithTokenHttp({
    url: '/api/v1/datalab/analysis_full',
    method: 'post',
    data: buildChartCalculationPayload(param),
    timeout: CHART_CALCULATION_TIMEOUT_MS,
  } as TDatalabHttpConfig);

/**
 * 作用：旧版全量分析计算接口（analysis_fullV2），保留兼容。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getDataAnlysisFull2
 * 原始引用（used-by）：（待后续 grep 完整补齐）
 */
export const getDataAnlysisFull2: TChartCalculationHandler<
  IAnalysisChartRequest,
  IAnalysisChartResponseDto
> = (param) =>
  datalabWithTokenHttp({
    url: '/api/v1/datalab/analysis_fullV2',
    method: 'post',
    data: buildChartCalculationPayload(param),
    timeout: CHART_CALCULATION_TIMEOUT_MS,
  } as TDatalabHttpConfig);

const analysisChartHandlerRegistry = buildChartHandlerRegistry<
  IAnalysisChartRequest,
  IAnalysisChartResponseDto
>(analysisChartConfigByType);

const dispatchAnalysisChart = createChartCalculationDispatcher<
  IAnalysisChartRequest,
  IAnalysisChartResponseDtoMap
>({
  registry: analysisChartHandlerRegistry,
  fallback: getDataAnlysisFull,
});

/**
 * 根据 chartType 分发分析页图表计算（analysis_chart_* 系列）。
 * 未命中专用映射时回退至 getDataAnlysisFull。
 *
 * 返回 {@link TAnalysisChartResponseDtoOf}（runtime 经 {@link normalizeResponseDto} 兜底）。
 */
export function calculateAnalysisChart<K extends TAnalysisChartMapKey>(
  chartType: K,
  request: IAnalysisChartRequest,
): Promise<TAnalysisChartResponseDtoOf<K>> {
  return dispatchAnalysisChart(chartType, request).then(data =>
    normalizeResponseDto<K>(data),
  );
}

/**
 * 分析页图表计算（VO 包装版）。
 *
 * 上传：`prepareAnalysisChartRequest` → wire ·
 * 下传：`calculateAnalysisChart`（Dto）→ `transformAnalysisChartResponseDtoToVo`（Vo）。
 */
export const calculateAnalysisChartVo = <K extends keyof IAnalysisChartUploadVoMap>(
  chartType: K,
  payload: TAnalysisChartUploadVo<K>,
): Promise<TAnalysisChartResultVoOf<K>> => {
  const request = prepareAnalysisChartRequest(chartType, payload);

  return calculateAnalysisChart(chartType, request).then(dto =>
    transformAnalysisChartResponseDtoToVo(chartType, dto),
  );
};
